/**
 * Searching everything this workspace has collected.
 *
 * **The capability the datastore was chosen for.** ADR-0005 justifies a single PostgreSQL
 * primary partly on its built-in full-text search, and `ARCHITECTURE_LOCK.md` §2.3 names
 * Postgres FTS as the v1 technology — yet no search existed. An analyst could only scroll
 * one case's Results tab, and `saved_search` stored queries nothing could run.
 *
 * Every hit leads somewhere. An assertion links straight to its provenance drawer, so a
 * finding reaches its evidence in one click; the others link to the case that holds them.
 * A result that could not be traced back would be a rumour with a citation-shaped hole.
 *
 * **Saved searches run through this same form.** A stored query is the parameter set the
 * endpoint takes, so applying one fills the controls and submits — there is no second
 * execution path, and therefore no second place where tenant scoping could be got wrong.
 * A saved search may carry filters this form cannot edit (a case, a date range); those are
 * shown as removable chips rather than applied invisibly, because a filter you cannot see
 * is a filter you cannot account for when reading the results.
 */

import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { Paged, SavedSearch, SavedSearchQuery, SavedSearchRequest, SearchHit } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';

const KINDS = [
  { value: '', label: 'Everything' },
  { value: 'assertion', label: 'Claims' },
  { value: 'subject', label: 'Subjects' },
  { value: 'note', label: 'Notes' },
  { value: 'case', label: 'Cases' },
];

/**
 * The filters a saved search can carry that this form has no control for.
 *
 * The search endpoint accepts them and the API stores them, so a saved search created
 * through the API can hold one. Dropping them on apply would run a different search from
 * the one that was saved; editing them here would be a filter builder, which is a larger
 * thing than this screen. So they are carried, displayed, and removable.
 */
type CarriedFilters = Pick<SavedSearchQuery, 'case_id' | 'occurred_after' | 'occurred_before'>;

const NO_CARRIED: CarriedFilters = {
  case_id: null,
  occurred_after: null,
  occurred_before: null,
};

/** The search view. */
export function SearchPage(): ReactElement {
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [carried, setCarried] = useState<CarriedFilters>(NO_CARRIED);

  const results = useInfiniteQuery({
    queryKey: ['search', query, kind, carried],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request<Paged<SearchHit>>('/search', {
        query: {
          q: query,
          limit: 25,
          cursor: pageParam,
          kind: kind || undefined,
          // `request` drops a null or undefined query value, so an unset filter stays
          // unset rather than being sent as the string "null".
          ...carried,
        },
      }),
    getNextPageParam: (last) => last.page.next_cursor ?? undefined,
    // Nothing is searched until something is typed: an empty query would be a full scan
    // dressed up as a page load.
    enabled: query.trim().length > 0,
  });

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    setQuery(draft.trim());
  }

  /**
   * Run a stored search by filling in this form.
   *
   * @param stored - The saved query.
   */
  function apply(stored: SavedSearchQuery): void {
    setDraft(stored.q);
    setQuery(stored.q);
    setKind(stored.kind ?? '');
    setCarried({
      case_id: stored.case_id ?? null,
      occurred_after: stored.occurred_after ?? null,
      occurred_before: stored.occurred_before ?? null,
    });
  }

  const current: SavedSearchQuery = {
    q: query,
    kind: (kind || null) as SavedSearchQuery['kind'],
    ...carried,
  };

  const rows = results.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Search</h1>

      <Card>
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3 p-3">
          <label className="block flex-1 text-sm">
            <span className="text-muted">Find</span>
            <input
              data-testid="search-input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="A name, an address, a domain, a phrase…"
              className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-muted">In</span>
            <select
              data-testid="search-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              {KINDS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" tone="primary" data-testid="search-submit">
            Search
          </Button>
        </form>
        <CarriedChips filters={carried} onClear={(field) => setCarried({ ...carried, [field]: null })} />
        <p className="px-3 pb-3 text-xs text-muted">
          Quoted phrases and <span className="font-mono">-exclusions</span> work. Identifiers
          are indexed whole and in parts, so <span className="font-mono">acme</span> finds{' '}
          <span className="font-mono">acme.example</span>.
        </p>
      </Card>

      <SavedSearches current={current} onApply={apply} />

      {results.error != null && <ErrorState error={results.error} />}
      {results.isFetching && !results.isFetchingNextPage && <Loading />}

      {query && !results.isFetching && (
        <Table
          headers={['Where', 'What', 'Case', 'When', '']}
          empty={`Nothing matches ${query}.`}
          rows={rows.map((hit) => [
            <Badge key="k" tone={hit.kind === 'assertion' ? 'accent' : 'muted'}>
              {hit.kind}
            </Badge>,
            <span key="l" data-testid="search-hit" className="break-all text-sm">
              {hit.label}
              {hit.context && (
                <span className="ml-2 font-mono text-xs text-muted">{hit.context}</span>
              )}
            </span>,
            <Link key="c" to={`/cases/${hit.case_id}`} className="text-xs text-accent">
              {hit.case_title}
            </Link>,
            <span key="t" className="text-xs text-muted">
              {hit.occurred_at.slice(0, 10)}
            </span>,
            // The provenance route: a claim goes to its evidence, everything else to the
            // case that holds it.
            <Link
              key="g"
              data-testid="search-hit-link"
              className="text-xs text-accent"
              to={
                hit.assertion_id
                  ? `/cases/${hit.case_id}/evidence?assertion=${hit.assertion_id}`
                  : `/cases/${hit.case_id}`
              }
            >
              {hit.assertion_id ? 'Evidence →' : 'Open →'}
            </Link>,
          ])}
        />
      )}

      {results.hasNextPage && (
        <Button
          data-testid="load-more-search"
          disabled={results.isFetchingNextPage}
          onClick={() => void results.fetchNextPage()}
        >
          {results.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}

/**
 * The filters an applied saved search brought with it that this form cannot edit.
 *
 * Shown rather than applied silently. An analyst reading a result set has to be able to
 * account for why a claim they expected is absent, and an invisible case or date filter
 * makes that impossible.
 *
 * @param props - The carried filters and a way to drop one.
 * @returns The chips, or nothing when no filter is carried.
 */
function CarriedChips({
  filters,
  onClear,
}: {
  filters: CarriedFilters;
  onClear: (field: keyof CarriedFilters) => void;
}): ReactElement | null {
  const shown: Array<[keyof CarriedFilters, string]> = [];
  if (filters.case_id) shown.push(['case_id', `case ${filters.case_id.slice(0, 8)}`]);
  if (filters.occurred_after) shown.push(['occurred_after', `after ${filters.occurred_after.slice(0, 10)}`]);
  if (filters.occurred_before) shown.push(['occurred_before', `before ${filters.occurred_before.slice(0, 10)}`]);
  if (shown.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 pb-3" data-testid="carried-filters">
      <span className="text-xs text-muted">Also filtering by</span>
      {shown.map(([field, label]) => (
        <button
          key={field}
          type="button"
          title={`Remove the ${field.replace('_', ' ')} filter`}
          onClick={() => onClear(field)}
          className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-xs"
        >
          <span className="font-mono">{label}</span>
          <span aria-hidden>×</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Searches this analyst has stored, and the control that stores the current one.
 *
 * **Saved searches are per-analyst working material, like notes.** They are listed for
 * their owner alone, and applying one runs it through the same form and the same endpoint
 * as any other search — the stored object *is* the request.
 *
 * @param props - The search currently in the form, and how to apply a stored one.
 * @returns The panel.
 */
function SavedSearches({
  current,
  onApply,
}: {
  current: SavedSearchQuery;
  onApply: (query: SavedSearchQuery) => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const saved = useQuery({
    queryKey: ['saved-searches'],
    queryFn: () => request<SavedSearch[]>('/saved-searches'),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['saved-searches'] });
  };

  const store = useMutation({
    mutationFn: () =>
      request<SavedSearch, SavedSearchRequest>('/saved-searches', {
        method: 'POST',
        // Sent as the typed query the API stores, so what is saved is what will run.
        body: { name: name.trim(), query: current },
      }),
    onSuccess: () => {
      setName('');
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => request<void>(`/saved-searches/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  // Nothing to save until something has been searched. Saving an empty query would store
  // a name with nothing behind it, which is the state migration 0019 cleared out.
  const savable = current.q.trim().length > 0 && name.trim().length > 0;

  return (
    <Card title="Saved searches">
      <div className="space-y-3 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="block flex-1 text-sm">
            <span className="text-muted">Save this search as</span>
            <input
              data-testid="saved-search-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={current.q ? `e.g. ${current.q}` : 'Search for something first'}
              className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            />
          </label>
          <Button
            tone="secondary"
            data-testid="save-search"
            disabled={!savable || store.isPending}
            onClick={() => store.mutate()}
          >
            {store.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>

        {/* A duplicate name is something a person does, not a server fault, and the API
            answers 409 rather than 500. Showing it here is the whole reason the mutation
            surfaces its error instead of swallowing it. */}
        {store.error != null && <ErrorState error={store.error} />}
        {remove.error != null && <ErrorState error={remove.error} />}

        {saved.isLoading && <Loading label="Reading saved searches" />}
        {saved.error != null && <ErrorState error={saved.error} />}

        {saved.data?.length === 0 && (
          <p className="text-xs text-muted">
            None yet. A saved search stores the term and the filters, not the results — it
            re-runs against whatever the workspace holds when you open it.
          </p>
        )}

        <ul className="space-y-1">
          {saved.data?.map((item) => (
            <li
              key={item.id}
              data-testid="saved-search"
              className="flex flex-wrap items-center gap-2 text-sm"
            >
              <button
                type="button"
                data-testid="apply-saved-search"
                onClick={() => onApply(item.query)}
                className="text-accent"
              >
                {item.name}
              </button>
              <span className="font-mono text-xs text-muted">{describe(item.query)}</span>
              <button
                type="button"
                data-testid="delete-saved-search"
                title={`Delete ${item.name}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(item.id)}
                className="text-xs text-muted"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

/**
 * Summarise a stored query in one line.
 *
 * The whole filter, not just the term: a name is what an analyst chose to call it, and two
 * searches called something similar are told apart by what they actually do.
 *
 * @param query - The stored query.
 * @returns A short description.
 */
function describe(query: SavedSearchQuery): string {
  const parts = [query.q];
  if (query.kind) parts.push(`in ${query.kind}`);
  if (query.case_id) parts.push(`case ${query.case_id.slice(0, 8)}`);
  if (query.occurred_after) parts.push(`after ${query.occurred_after.slice(0, 10)}`);
  if (query.occurred_before) parts.push(`before ${query.occurred_before.slice(0, 10)}`);
  return parts.join(' · ');
}
